import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { getNotificationEventChannelDetails } from 'services/admin';

import { TextLabel } from 'uiToolkitV1/Typography';

import { NotificationChannels } from './NotificationChannels';
import { NotificationEvent } from './NotificationEvent';
import { NotificationSettingsFooter } from './NotificationFooter';
import { NotificationLoading } from './NotificationLoading';
import type { ChannelDetails, ChannelEventDetails } from './types';

export const NotificationSettingsIntent = ({
  isEmployeeNotificationSettings,
}: {
  isEmployeeNotificationSettings?: boolean;
}) => {
  const { l1Module } = useRouteToNavLevels();
  const isMyData = l1Module === 'myData';
  const { enabledFeatures = [] } = useSelector(selectLoggedInEmployeeData);

  // send page view event
  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: isMyData ? 'Me-settings-notifications' : 'Company-settings-notifications',
      },
    });
  }, [isMyData]);

  const { data, isLoading, refetch } = useQuery<{
    channelDetailsArray: ChannelDetails[];
    channelEventDetailsArray: ChannelEventDetails[];
  }>({
    queryKey: ['getNotificationEventChannelDetails', isEmployeeNotificationSettings, enabledFeatures],
    queryFn: () => getNotificationEventChannelDetails({ isEmployeeNotificationSettings, enabledFeatures }),
  });

  if (isLoading) {
    return <NotificationLoading />;
  }

  const { channelDetailsArray, channelEventDetailsArray } = data;

  return (
    <>
      {isNetchexLogin && (
        <TextLabel bold py="12px">
          Note: Changes made here will apply across all companies in the organization.
        </TextLabel>
      )}
      <NotificationChannels
        isEmployeeNotificationSettings={isEmployeeNotificationSettings}
        channelDetailsArray={channelDetailsArray}
        refreshData={refetch}
      />
      <NotificationEvent
        isEmployeeNotificationSettings={isEmployeeNotificationSettings}
        channelDetailsArray={channelDetailsArray}
        channelEventDetailsArray={channelEventDetailsArray}
        refreshData={refetch}
      />
      <NotificationSettingsFooter
        isEmployeeNotificationSettings={isEmployeeNotificationSettings}
        refreshData={refetch}
      />
    </>
  );
};
